<template>
  <div id="receiptConfiguration">
    <PageTitle :title="$t('receipt_config.title')" :subtitle="$t('receipt_config.subtitle')"></PageTitle>
    <PageContainer v-if="company && config">
      <div class="header">
        <div class="header__title">{{$t('receipt_config.header_title')}}</div>
        <div class="header__description">{{$t('receipt_config.header_description')}}</div>
      </div>
      <div class="columns">
        <div class="column">
          <div class="group">
            <label>{{$t('receipt_config.header_lines')}}</label>
            <div class="input__group" v-for="(line, lineIndex) of config.headerLines">
              <div class="input">
                <input type="text" v-model="config.headerLines[lineIndex]">
                <MaxSize v-model="config.headerLines[lineIndex]" :max-size="40"/>
              </div>
              <div class="action">
                <button class="btn" @click="removeReceiptHeaderLine(lineIndex)">
                  <svgicon src="icon/close.svg" :height="15" :width="15"></svgicon>
                </button>
              </div>
            </div>
            <div class="input__action" @click="addReceiptHeaderLine">
              <button class="btn btn-primary">
                {{$t('receipt_config.add_line')}}
              </button>
            </div>
          </div>
          <div class="group">
            <label>{{$t('receipt_config.footer_lines')}}</label>
            <div class="input__group" v-for="(line, lineIndex) of config.footerLines">
              <div class="input">
                <input type="text" v-model="config.footerLines[lineIndex]">
                <MaxSize v-model="config.footerLines[lineIndex]" :max-size="40"/>
              </div>
              <div class="action">
                <button class="btn" @click="removeReceiptFooterLine(lineIndex)">
                  <svgicon src="icon/close.svg" :height="15" :width="15"></svgicon>
                </button>
              </div>
            </div>
            <div class="input__action" @click="addReceiptFooterLine">
              <button class="btn btn-primary">
                {{$t('receipt_config.add_line')}}
              </button>
            </div>
          </div>
          <br>
          <div class="group group--toggle">
            <div class="left">
              <div class="group__title">{{$t('receipt_config.limit_product_description_title')}}</div>
              <div class="group__description">{{$t('receipt_config.limit_product_description_description')}}</div>
            </div>
            <div class="right">
              <ToggleSwitch v-model="config.limitProductDescription"></ToggleSwitch>
            </div>
          </div>
          <div class="group group--toggle">
            <div class="left">
              <div class="group__title">{{$t('receipt_config.hide_employee_info_title')}}</div>
              <div class="group__description">{{$t('receipt_config.hide_employee_info_description')}}</div>
            </div>
            <div class="right">
              <ToggleSwitch v-model="config.hideEmployeeInformation"></ToggleSwitch>
            </div>
          </div>
          <div class="group group--toggle">
            <div class="left">
              <div class="group__title">{{$t('receipt_config.hide_free_modifiers_title')}}</div>
              <div class="group__description">{{$t('receipt_config.hide_free_modifiers_description')}}</div>
            </div>
            <div class="right">
              <ToggleSwitch v-model="config.hideFreeModifiers"></ToggleSwitch>
            </div>
          </div>
          <!--<div class="group group--toggle">
            <div class="left">
              <div class="group__title">{{$t('receipt_config.info_at_bottom_title')}}</div>
              <div class="group__description">{{$t('receipt_config.info_at_bottom_description')}}</div>
            </div>
            <div class="right">
              <ToggleSwitch v-model="config.informationAtBottom"></ToggleSwitch>
            </div>
          </div>-->
          <br>
          <ImageUpload @changed="config.logo = $event" identifier="bitmap-logo" :object="config.logo"
                       :formats="['image/bmp']" :previewHeight="130" :maxSizeInMb="0.2"></ImageUpload>
        </div>
        <div class="column">
          <label>{{$t('receipt_config.preview')}}</label>
          <div class="receipt-preview__wrapper">
            <div class="receipt-preview">
              <template v-if="config.logo && config.logo.url">
                <div class="logo" v-if="config.logo && config.logo.url">
                  <img :src="config.logo.url" alt="Logo">
                </div>
              </template>
              <div class="center">
                <template v-if="config.headerLines.find(l => !!l)">
                  <div v-for="line of config.headerLines">{{line}}</div>
                </template>
                <template v-else>
                  <div>{{company.name}}</div>
                  <div>{{company.address.address}}</div>
                  <div>{{(company.address.city || "") + " " + (company.address.state || "") + "  " + (company.address.zipcode || "")}}</div>
                  <div v-if="company.phoneNumber">{{company.phoneNumber}}</div>
                  <div v-if="company.email">{{company.email}}</div>
                </template>
              </div>
              <div class="split">{{'-'.repeat(48)}}</div>
              <div class="center">{{now}}</div>
              <div class="center big">{{$t('receipt_config.check')}} #00000000</div>
              <div class="center">{{$t('sales.order')}} #000</div>
              <div class="split">{{'-'.repeat(48)}}</div>
              <div class="between" v-for="n in 4">
                  <div class="left">00x&nbsp;&nbsp;&nbsp;Lorem Ipsum</div>
                  <div class="right">00.00</div>
              </div>
              <div>&nbsp;</div>
              <div class="between">
                <div class="left">{{$t('sales.subtotal').toUpperCase()}}</div>
                <div class="right">00.00</div>
              </div>
              <div class="between">
                <div class="left">{{$t('tax.title').toUpperCase()}} A (#0000000000)</div>
                <div class="right">00.00</div>
              </div>
              <div class="between">
                <div class="left">{{$t('tax.title').toUpperCase()}} B (#0000000000)</div>
                <div class="right">00.00</div>
              </div>
              <div>&nbsp;</div>
              <div class="between big">
                <div class="left">{{$t('sales.total')}}</div>
                <div class="right">00.00</div>
              </div>
              <div class="between">
                <div class="left">{{$t('payment').toUpperCase()}}</div>
                <div class="left">{{$t('payment').toUpperCase()}}</div>
                <div class="right">00.00</div>
              </div>
              <div>&nbsp;</div>
              <div class="center" v-for="line of config.footerLines">{{line}}</div>
              <div v-if="config.footerLines.find(l => !!l)">&nbsp;</div>
              <template v-if="!config.hideEmployeeInformation">
                <div class="center">{{$t('receipt_config.served_by')}}</div>
                <div class="center">Laurent Ipsum</div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
    <PageFooter :mainAction="{action: save, text: $t('save')}"></PageFooter>
  </div>
</template>

<script>
  import PageContainer from "@/components/PageContainer";
  import PageTitle from "@/components/PageTitle";
  import MaxSize from "@/components/tools/MaxSize";
  import ToggleSwitch from "@/components/tools/ToggleSwitch";
  import PageFooter from "@/components/PageFooter";
  import ImageUpload from "@/components/tools/ImageUpload";
  import moment from "moment";
  import {Core} from "@/core/Core";

  export default {
    name: "receiptConfiguration",
    components: { PageContainer, PageTitle, MaxSize, ToggleSwitch, ImageUpload, PageFooter },

    data() {
      return {
        company: null,
        config: {
          headerLines: [],
          footerLines: [],
          limitProductDescription: false,
          hideEmployeeInformation: false,
          informationAtBottom: false,
          hideFreeModifiers: false,
          logo: null
        }
      }
    },

    computed: {
      now() {
        return moment().format("YYYY/MM/DD - HH:mm:ss");
      }
    },

    async mounted() {
      showSpinner();
      this.$store.state.pageTitleTranslationKey = "receipt_config.title";
      this.$store.state.pageIcon = "/icon/printer.svg";
      this.company = await this.$store.getters.getContextCompany;
      await this.loadConfiguration();
      hideSpinner();
    },

    methods: {
      async loadConfiguration() {
        try {
          let response = await Core.request("GET","/api/pos_data_storage/receipt_configuration?company=" + this.company.id);
          if (response && response.data) {
            let config = response.data;
            this.config.headerLines = config.headerLines || [];
            this.config.footerLines = config.footerLines || [];
            this.config.limitProductDescription = config.limitProductDescription || false;
            this.config.hideEmployeeInformation = config.hideEmployeeInformation || false;
            this.config.informationAtBottom = config.informationAtBottom || false;
            this.config.hideFreeModifiers = config.hideFreeModifiers || false;
            this.config.logo = config.logo || { url: "" }
          }
        } catch (e) {
          console.log(e);
        }
      },
      async save() {
        let data = JSON.parse(JSON.stringify(this.config));
        data.logo = data.logo ? { url: data.logo.url } : null;
        try {
          showSpinner();
          let response = await Core.request("PUT", "/api/pos_data_storage/receipt_configuration?company=" + this.company.id, {
            data: data
          });
          hideSpinner();
          if (response) {
            toast(this.$t('receipt_config.save_success'), "success", this.$t("success"));
          }
        } catch (e) {
          hideSpinner();
          console.log(e);
          toast(this.$t("receipt_config.save_error"), "error", this.$t("error.title"));
        }
      },
      addReceiptHeaderLine() {
        this.config.headerLines.push("");
      },
      addReceiptFooterLine() {
        this.config.footerLines.push("");
      },
      removeReceiptHeaderLine(index) {
        this.config.headerLines.splice(index, 1);
      },
      removeReceiptFooterLine(index) {
        this.config.footerLines.splice(index, 1);
      },
    }
  }
</script>

<style lang="scss" scoped>
  $bottom-edge: calc(100% - 1.5%);
  $top-edge: calc(0% + 1.5%);
  $bottom: 100%;
  $top: 0%;

  .receipt-preview__wrapper {
    filter: drop-shadow(0px 0px 3px rgba(0,0, 0, 0.2));
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .receipt-preview {
    width: 375px;
    flex-grow: 1;
    margin-top: 10px;
    background-color: white;
    font-family: 'Roboto Mono', monospace;
    padding: 40px;
    font-size: 10px;
    color: #1a1a1a;
    clip-path: polygon(
            $top $top,
            0% $bottom, 5% $bottom-edge, 10% $bottom, 15% $bottom-edge, 20% $bottom, 25% $bottom-edge, 30% $bottom, 35% $bottom-edge, 40% $bottom, 45% $bottom-edge,
            50% $bottom, 55% $bottom-edge, 60% $bottom, 65% $bottom-edge, 70% $bottom, 75% $bottom-edge, 80% $bottom, 85% $bottom-edge, 90% $bottom, 95% $bottom-edge,
            $bottom $bottom,
            100% $top, 95% $top-edge, 90% $top, 85% $top-edge, 80% $top, 75% $top-edge, 70% $top, 65% $top-edge, 60% $top,
            55% $top-edge, 50% $top, 45% $top-edge, 40% $top, 35% $top-edge, 30% $top, 25% $top-edge, 20% $top, 15% $top-edge, 10% $top, 5% $top-edge,
            $top $top);

    .big {
      font-size: 20px;
    }

    .between {
      display: flex;
      justify-content: space-between;
    }

    .center {
      text-align: center;
    }

    .split {
      margin-top: 15px;
      margin-bottom: 15px;
    }

    .logo {
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        max-width: 250px;
        height: auto;
      }
    }

  }

  .header {
    margin-bottom: 20px;

    .header__title {
      font-weight: bold;
      font-size: 16px;
      color: #404040;
    }
    .header__description {
      font-size: 14px;
      color: #808080;
    }
  }

  .page-container {
    display: flex;
    flex-direction: column;

    .columns {
      flex-grow: 1;

      .column {
        display: flex;
        flex-direction: column;
      }
    }
  }

  .group {
    &.group--toggle {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
    .group__title {
      color: #1A1A1A;
      font-size: 16px;
    }
    .group__description {
      font-size: 14px;
      margin-top: 2px;
      color: #808080;
    }
  }

  .input__group {
    display: flex;
    padding-bottom: 10px;
    .input {
      flex-grow: 1;

      input {
        width: 100%;
        border-right: none;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    .action {
      .btn {
        border: 1px solid #d9d9d9;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        margin-bottom: 0;
        height: 43px;
        fill: #d9d9d9;
        background-color: #f1f1f1;
        padding: 10px 15px;
      }
    }
  }

  #receiptConfiguration {
    display: flex;
    flex-direction: column;
  }
</style>